import { useQuery } from "@apollo/client";
import { Alert, CircularProgress, Snackbar, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import { Loader } from "@/pages/UserDetails/styles";
import { NoData } from "@/styles/flex";

import { EditTreatmentModal } from "../EditTreatmentModal";
import { createTreatmentHistoryColumnsWithEdit } from "./columns";
import { GET_PATIENT_TREATMENT_HISTORY_TABLE } from "./queries";
import { customTableStyles } from "./styles";
import { TreatmentHistoryResponse, TreatmentHistoryRow, TreatmentHistoryTableProps } from "./types";

const CustomNoDataOverlay = () => {
  return (
    <NoData>
      <Typography>No Program history records found</Typography>
    </NoData>
  );
};

const toTreatmentHistoryRows = (
  items: TreatmentHistoryResponse["treatmentHistory"] | undefined
): TreatmentHistoryRow[] =>
  (items ?? []).map((item) => ({
    id: item.id,
    programType: item.program_type,
    roundNumber: item.round,
    startDate: item.start_date,
    endDate: item.end_date,
    specialist: item.specialist,
    status: item.status,
  }));

export const TreatmentHistoryTable: React.FC<TreatmentHistoryTableProps> = ({ userId }) => {
  const [showError, setShowError] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedTreatment, setSelectedTreatment] = useState<TreatmentHistoryRow | null>(null);

  const { data, loading, error, refetch } = useQuery<TreatmentHistoryResponse>(
    GET_PATIENT_TREATMENT_HISTORY_TABLE,
    {
      variables: { accountId: userId },
      // Skip cache because other queries hit resolvers that fetch the DB status from programHistory
      // as opposed to computed one we want
      fetchPolicy: "no-cache",
      skip: !userId,
    }
  );

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  const tableRows = useMemo(
    () => toTreatmentHistoryRows(data?.treatmentHistory),
    [data?.treatmentHistory]
  );

  const handleEdit = (row: TreatmentHistoryRow) => {
    setSelectedTreatment(row);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedTreatment(null);
  };

  const handleEditSuccess = () => {
    refetch();
  };

  const columnsWithEdit = createTreatmentHistoryColumnsWithEdit(handleEdit);

  if (loading) {
    return (
      <Loader>
        <CircularProgress />
      </Loader>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <StyledDataTable
        columns={columnsWithEdit}
        data={tableRows}
        noDataComponent={<CustomNoDataOverlay />}
        customStyles={customTableStyles}
      />

      {editModalOpen && (
        <EditTreatmentModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          treatmentData={selectedTreatment}
          onSuccess={handleEditSuccess}
          account={userId}
        />
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch treatment history data.
        </Alert>
      </Snackbar>
    </div>
  );
};
